import { useEffect, useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';
import { Button } from '@/design/components';

/**
 * צילום ת"ז דרך המצלמה של המכשיר.
 *
 * getUserMedia ולא רק input type=file capture: ב capture המצלמה
 * נפתחת רק בטלפון, ומי שיושב בקונסולה מול מחשב עם מצלמה לא קיבל
 * כלום. כאן שניהם עובדים מאותו כפתור.
 *
 * facingMode אחורית כברירת מחדל, כי מצלמים מסמך ולא פנים. במחשב
 * הבקשה הזו פשוט מתעלמת ומקבלים את המצלמה היחידה.
 *
 * זה צילום, לא סריקה: אין כאן זיהוי קצוות ויישור פרספקטיבה.
 * מי שמצלם בזווית יקבל תמונה בזווית.
 */
export function CameraCapture({
  onCapture,
  onCancel,
}: {
  onCapture: (file: Blob) => void;
  onCancel: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          setIsReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('אין גישה למצלמה. אפשר לאשר אותה בדפדפן, או להעלות קובץ במקום.');
        }
      });

    // עצירת הזרם ביציאה, אחרת נורית המצלמה נשארת דולקת
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  const shoot = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) onCapture(blob);
      },
      'image/jpeg',
      0.9,
    );
  };

  return (
    <div className="grid gap-2 rounded-md border border-line bg-raised p-3">
      {error ? (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      ) : (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            aria-label="תצוגת המצלמה"
            className="w-full rounded-sm bg-charcoal"
          />
          <p className="m-0 text-body-sm text-muted">
            למלא את המסגרת בתעודה, להחזיק יציב, ולצלם. זה צילום רגיל — כדאי לוודא שהכל קריא.
          </p>
        </>
      )}

      <div className="flex flex-wrap gap-2">
        {!error && (
          <Button variant="primary" disabled={!isReady} onClick={shoot}>
            <Camera size={18} strokeWidth={1.75} aria-hidden="true" />
            צילום
          </Button>
        )}
        <Button variant="secondary" onClick={onCancel}>
          <X size={18} strokeWidth={1.75} aria-hidden="true" />
          סגירת המצלמה
        </Button>
      </div>
    </div>
  );
}
